import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, ArrowLeft, Loader2, Mail, Lock, Stethoscope } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import { signUp } from '@/lib/supabase/queries/auth';
import { useTranslation } from 'react-i18next';

export default function SignUpPage() {
  const { t } = useTranslation('doctor-platform');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const { toast } = useToast();

  const validateForm = () => {
    if (!email || !password || !confirmPassword) {
      setError(t('auth.signup.validation.allFields'));
      return false;
    }

    if (password.length < 6) {
      setError(t('auth.signup.validation.passwordLength'));
      return false;
    }

    if (password !== confirmPassword) {
      setError(t('auth.signup.validation.passwordMismatch'));
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('auth.signup.validation.invalidEmail'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Step 1: Create auth account only
      const result = await signUp(email, password);
      
      if (result.error) {
        throw result.error;
      }
      
      if (result.data) {
        toast({
          title: t('auth.signup.toast.title'),
          description: t('auth.signup.toast.description'),
        });
        
        // Navigate to profile setup
        navigate('/auth/setup-profile');
      }
    } catch (error: any) {
      console.error('Sign up error:', error);
      setError(error.message || t('auth.signup.validation.createFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center p-8 sm:p-12 lg:p-16 bg-white dark:bg-gray-900">
        <div className="mx-auto w-full max-w-md space-y-8">
          {/* Back to login */}
          <Link
            to="/auth/login"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('auth.signup.backToLogin')}
          </Link>

          {/* Title */}
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Stethoscope className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
              {t('auth.signup.title')}
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t('auth.signup.subtitle')}
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Sign-up Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email">{t('auth.signup.emailLabel')}</Label>
              <div className="mt-1 relative">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder={t('auth.signup.emailPlaceholder')}
                  className="pl-10"
                  disabled={isLoading}
                  autoComplete="email"
                />
                <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <div>
              <Label htmlFor="password">{t('auth.signup.passwordLabel')}</Label>
              <div className="mt-1 relative">
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder={t('auth.signup.passwordPlaceholder')}
                  className="pl-10"
                  disabled={isLoading}
                  minLength={6}
                />
                <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">{t('auth.signup.confirmPasswordLabel')}</Label>
              <div className="mt-1 relative">
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder={t('auth.signup.confirmPasswordPlaceholder')}
                  className="pl-10"
                  disabled={isLoading}
                />
                <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('auth.signup.submitting')}
                </>
              ) : (
                t('auth.signup.submit')
              )}
            </Button>

            <div className="text-center text-sm">
              <span className="text-muted-foreground">
                {t('auth.signup.loginPrompt')}{' '}
              </span>
              <Link 
                to="/auth/login" 
                className="text-primary hover:underline font-medium"
              >
                {t('auth.signup.login')}
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Right side - Medical themed gradient */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-500 to-teal-400">
          <div className="absolute inset-0 bg-black/20" />
        </div>
        <div className="relative h-full flex items-center justify-center p-16 text-white">
          <div className="max-w-xl text-center">
            <Stethoscope className="h-20 w-20 mx-auto mb-8 opacity-90" />
            <h2 className="text-4xl font-bold mb-6">
              {t('auth.signup.heroTitle')}
            </h2>
            <p className="text-xl opacity-90 leading-relaxed">
              {t('auth.signup.heroBody')}
            </p>
            <div className="mt-8 flex justify-center space-x-4">
              <div className="flex items-center">
                <div className="h-2 w-2 bg-white rounded-full mr-2" />
                <span className="text-sm opacity-75">{t('auth.signup.compliance')}</span>
              </div>
              <div className="flex items-center">
                <div className="h-2 w-2 bg-white rounded-full mr-2" />
                <span className="text-sm opacity-75">{t('auth.signup.securePlatform')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
